import React, { useState } from 'react';
import { Select } from '@zoopla-eng/zds-components/select';
import { InputField } from '@zoopla-eng/zds-components/input-field';
import { Button, ButtonLeft } from '@zoopla-eng/zds-components/button';
import { Filters, OPTIONS } from './constants';
import { Grid } from '@zoopla-eng/zds-components/grid';
import {
    ChevronDownMedium,
    ChevronRightMedium
} from '@zoopla-eng/zds-components/icons';
import {
    customGridStyle,
    errorMessageStyle,
    gridItemWithPaddingStyle
} from './SearchBar.styles.css';
import {
    gridItemMobileAndTabletFilterStyle,
    gridItemMobileFilterStyle,
    styledFiltersButtonContainerStyle
} from './SearchBar.styles.css';

import { fontGroups } from '@zoopla-eng/zds-components/themes';

export const LOCATION_LABEL_TEXT = 'Location';
export const LOCATION_PLACEHOLDER_TEXT = 'Enter a location';
export const SEARCH_RADIUS_LABEL_TEXT = 'Search radius';
export const AGENT_TYPE_LABEL_TEXT = 'Type of agent';
export const COMPANY_NAME_LABEL_TEXT = 'Agent name';
export const COMPANY_NAME_PLACEHOLDER_TEXT = 'Enter a name';
export const SUBMIT_BTN_TEXT = 'Search';
export const FILTERS_TEXT = 'Filters';

export const SearchBar: React.FC<{ initialLocation?: string }> = ({
    initialLocation
}) => {
    const url = new URL(initialLocation ?? '/', 'https://www.zoopla.co.uk'); // valid domain required for constructor but irrelevant
    const [q, setQ] = useState(url.searchParams.get('q') ?? '');
    const [radius, setRadius] = useState(
        url.searchParams.get('radius') ??
            OPTIONS[Filters.radius][0].data?.['value']
    );
    const [section, setSection] = useState(
        url.searchParams.get('section') ??
            getSectionFromPath(url.pathname) ??
            OPTIONS[Filters.section][0].data?.['value']
    );
    const [agent, setAgent] = useState(
        url.searchParams.get('company_name') ?? ''
    );
    const [filterStatus, setFilterStatus] = useState<'closed' | 'open'>(
        'closed'
    );
    const [formState, setFormState] = useState<'initial' | 'emptyFormError'>(
        'initial'
    );

    const selectedRadiusOptionIdx = OPTIONS[Filters.radius].findIndex(
        ({ data }) => data?.['value'] === radius
    );
    const selectedSectionOptionIdx = OPTIONS[Filters.section].findIndex(
        ({ data }) => data?.['value'] === section
    );

    function validateForm(evt: React.FormEvent) {
        setFormState('initial');
        if (!q && !agent) {
            setFormState('emptyFormError');
            evt.preventDefault();
        }
    }

    return (
        <form method="get" action="/search/" onSubmit={validateForm}>
            <div className={customGridStyle}>
                <Grid.Item area="location">
                    <div className={gridItemWithPaddingStyle}>
                        <InputField
                            name="q"
                            label={LOCATION_LABEL_TEXT}
                            value={q}
                            onChange={(ev) => setQ(ev.target.value)}
                            placeholder={LOCATION_PLACEHOLDER_TEXT}
                            hasError={formState === 'emptyFormError'}
                        />
                    </div>
                </Grid.Item>
                <Grid.Item area="companyName">
                    <div
                        className={gridItemMobileFilterStyle({
                            filterStatus
                        })}
                    >
                        <InputField
                            key="company_name"
                            name="company_name"
                            label={COMPANY_NAME_LABEL_TEXT}
                            value={agent}
                            onChange={(ev) => setAgent(ev.target.value)}
                            placeholder={COMPANY_NAME_PLACEHOLDER_TEXT}
                            hasError={formState === 'emptyFormError'}
                        />
                    </div>
                </Grid.Item>
                <Grid.Item area="radius">
                    <div
                        className={gridItemMobileAndTabletFilterStyle({
                            filterStatus
                        })}
                    >
                        <Select
                            key="radius"
                            label={SEARCH_RADIUS_LABEL_TEXT}
                            options={OPTIONS[Filters.radius]}
                            selectedOption={
                                OPTIONS[Filters.radius][selectedRadiusOptionIdx]
                            }
                            onSelectedOptionChange={(ev) =>
                                setRadius(ev.data?.['value'])
                            }
                        />
                    </div>
                </Grid.Item>
                <Grid.Item area="section">
                    <div
                        className={gridItemMobileAndTabletFilterStyle({
                            filterStatus
                        })}
                    >
                        <Select
                            key="section"
                            label={AGENT_TYPE_LABEL_TEXT}
                            options={OPTIONS[Filters.section]}
                            selectedOption={
                                OPTIONS[Filters.section][
                                    selectedSectionOptionIdx
                                ]
                            }
                            onSelectedOptionChange={(ev) =>
                                setSection(ev.data?.['value'])
                            }
                        />
                    </div>
                </Grid.Item>
                <Grid.Item area="submitButton">
                    <div className={gridItemWithPaddingStyle}>
                        <Button type="submit" value="search" category="primary">
                            {SUBMIT_BTN_TEXT}
                        </Button>
                    </div>
                </Grid.Item>
                <Grid.Item area="error">
                    {formState === 'emptyFormError' && (
                        <div
                            className={`${errorMessageStyle} ${fontGroups.fork}`}
                        >
                            Please enter a Location or Agent name
                        </div>
                    )}
                </Grid.Item>
                <div
                    className={styledFiltersButtonContainerStyle({
                        filterStatus
                    })}
                >
                    <ButtonLeft
                        category="tertiary"
                        onClick={() =>
                            setFilterStatus((status) =>
                                status === 'closed' ? 'open' : 'closed'
                            )
                        }
                        type="button"
                        Icon={
                            filterStatus === 'open'
                                ? ChevronDownMedium
                                : ChevronRightMedium
                        }
                    >
                        {FILTERS_TEXT}
                    </ButtonLeft>
                </div>
                <input type="hidden" id="radius" name="radius" value={radius} />
                <input
                    type="hidden"
                    id="section"
                    name="section"
                    value={section}
                />
                <input
                    type="hidden"
                    id="search_source"
                    name="search_source"
                    value="find-agents"
                />
                <input
                    type="hidden"
                    id="geo_autocomplete_identifier"
                    name="geo_autocomplete_identifier"
                    value=""
                />
                <input
                    type="hidden"
                    id="view_type"
                    name="view_type"
                    value="list"
                />
            </div>
        </form>
    );
};

function getSectionFromPath(path: string): string | void {
    if (path.startsWith('/find-agents/estate-agents')) {
        return 'find-agents/estate-agents';
    }
    if (path.startsWith('/find-agents/letting-agents')) {
        return 'find-agents/letting-agents';
    }
}
